import { useState } from "react";
import { Link } from "react-router-dom";
import { Quote, PlayCircle, Loader2, AlertCircle, ArrowUpRight, RefreshCw, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";
import { fetchers, qk, prefetchWeeklyQuotesDetail } from "@/lib/queryClient";
import { toast } from "sonner";
import { McpBadge } from "@/pages/DemiQuotes";


function StatusPill({ status }) {
  const map = {
    review: { bg: "#EFF6FF", fg: "#1D4ED8", label: "Review" },
    sent:   { bg: "#ECFDF5", fg: "#047857", label: "Sent" },
    draft:  { bg: "#F1F5F9", fg: "#475569", label: "Draft" },
    failed: { bg: "#FEF2F2", fg: "#B91C1C", label: "Failed" },
    empty:  { bg: "#F1F5F9", fg: "#64748B", label: "Empty" },
    archived: { bg: "#F1F5F9", fg: "#94A3B8", label: "Archived" },
    imported_draft: { bg: "#EEF2FF", fg: "#4338CA", label: "Imported" },
  };
  const m = map[status] || map.draft;
  return (
    <span
      className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </span>
  );
}


export default function WeeklyQuotesList() {
  const [generating, setGenerating] = useState(false);
  const [feed, setFeed] = useState("hailmary");

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: qk.weeklyQuotes({ source: feed }),
    queryFn: () => fetchers.weeklyQuotes({ source: feed }),
    staleTime: 5 * 60_000,
    refetchInterval: 30_000,
  });
  const rows = data?.data ?? [];

  const generateNow = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/weekly-quotes-v2/generate`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({}),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || `HTTP ${r.status}`);
      if (body.status === "skipped_existing") {
        toast(`Weekly quotes already exist for ${body.week_label}. Open below.`);
      } else {
        toast.success(`Generation started for ${body.week_label}. Runs ~2-5 min.`);
        setTimeout(() => refetch(), 5000);
      }
    } catch (e) {
      toast.error(`Generate failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="weekly-quotes-page">
      <PageHeader
        title="Weekly Quotes"
        subtitle="Wed→Tue SGT window — three shareable branded PNG cards. Auto-delivered every Wednesday 06:00 SGT (08:00 Brisbane)."
      />
      <div
        role="tablist"
        aria-label="Quote source"
        data-testid="weekly-quotes-feed-toggle"
        className="inline-flex items-center gap-1 rounded-lg border border-hm-border bg-hm-bg-1 p-1 mb-5"
      >
        {[
          { id: "hailmary", label: "Hail Mary" },
          { id: "demi",     label: "Demi", mcp: true },
        ].map((t) => {
          const active = feed === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`weekly-quotes-feed-tab-${t.id}`}
              onClick={() => setFeed(t.id)}
              className={`mono uppercase tracking-widest text-[11px] px-3 py-1.5 rounded-md transition-colors inline-flex items-center gap-1.5 ${
                active
                  ? "bg-hm-accent-soft text-hm-accent-dim"
                  : "text-hm-fg-muted hover:text-hm-fg"
              }`}
            >
              {t.label}
              {t.mcp && <Sparkles className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <div className="hm-surface p-4 md:p-6" data-testid="weekly-quotes-panel">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="text-xs text-hm-fg-muted leading-relaxed max-w-xl">
            {feed === "demi" ? (
              <>
                Demi · Weekly Quotes pushed by Claude Pro via the Hail Mary MCP
                connector (<code className="mono">push_brief product=&quot;quotes&quot;</code>).
                No automated image generation, no cron delivery — view-only here.
              </>
            ) : (
              <>
                Product 4 · Weekly Quotes. Direct-quote extraction from
                interviews, panels, press releases &amp; podcasts. LLM-scored on
                opinionated / forward-looking / data-backed, with Beyond Activ
                customer-tier boost.
              </>
            )}
          </div>
          {feed === "hailmary" && (
            <button
              onClick={generateNow}
              disabled={generating}
              data-testid="weekly-quotes-generate-now"
              className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-hm-accent text-hm-bg text-xs font-medium hover:bg-hm-accent-dim disabled:opacity-40"
            >
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />}
              Generate weekly quotes now
            </button>
          )}
        </div>

        {isError && !data ? (
          <div
            className="p-6 rounded-lg border border-hm-border bg-hm-bg-1 flex items-start gap-4"
            data-testid="weekly-quotes-error"
          >
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#B45309" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-hm-fg font-medium">Unable to load weekly quotes right now</div>
              <p className="mono text-[11px] text-hm-fg-muted mt-1">
                {error?.status
                  ? `The server returned ${error.status}. This is usually transient.`
                  : "The connection failed. This is usually transient."}
              </p>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                data-testid="weekly-quotes-retry"
                className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium border border-hm-border text-hm-fg hover:border-hm-border-strong disabled:opacity-40"
              >
                {isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Retry
              </button>
            </div>
          </div>
        ) : rows.length === 0 && !isPending ? (
          <div className="py-10 text-center" data-testid="weekly-quotes-empty">
            {feed === "demi" ? (
              <>
                <Sparkles className="h-6 w-6 text-hm-fg-subtle mx-auto mb-3" />
                <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
                  No Demi quotes yet.
                </div>
                <p className="text-sm text-hm-fg-muted mt-2 max-w-md mx-auto">
                  Push from Claude.ai via{" "}
                  <code className="mono text-xs">push_brief product=&quot;quotes&quot;</code>.
                </p>
              </>
            ) : (
              <>
                <Quote className="h-6 w-6 text-hm-fg-subtle mx-auto mb-3" />
                <div className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
                  No weekly quotes yet.
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-hm-border overflow-hidden">
            <div className="grid grid-cols-[1.4fr_0.6fr_0.6fr_0.9fr_auto] gap-4 px-5 py-3 bg-hm-bg-1 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              <div>Week</div>
              <div>Status</div>
              <div className="text-right">Quotes</div>
              <div>Created</div>
              <div></div>
            </div>
            <div className="divide-y divide-hm-border">
              {rows.map((r) => (
                <Link
                  key={r.id}
                  to={`/weekly-quotes/${r.id}`}
                  onMouseEnter={() => prefetchWeeklyQuotesDetail(r.id)}
                  onFocus={() => prefetchWeeklyQuotesDetail(r.id)}
                  data-testid={`weekly-quotes-row-${r.id.slice(0,8)}`}
                  className="grid grid-cols-[1.4fr_0.6fr_0.6fr_0.9fr_auto] gap-4 px-5 py-4 items-center hover:bg-hm-bg-1/40 transition-colors"
                >
                  <div>
                    <div className="text-sm text-hm-fg font-medium">{r.week_label}</div>
                    <div className="mono text-[10px] text-hm-fg-subtle mt-0.5">
                      {r.week_start} → {r.week_end}
                    </div>
                  </div>
                  <div><StatusPill status={r.status} /></div>
                  <div className="text-right tabular-nums text-sm text-hm-fg">
                    {feed === "demi" ? (
                      <McpBadge />
                    ) : (
                      r.quote_count ?? 0
                    )}
                  </div>
                  <div className="mono text-[11px] text-hm-fg-muted">
                    {new Date(r.created_at).toLocaleString(undefined, {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </div>
                  <div className="text-hm-fg-subtle">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
